import {
  Group,
  Badge,
  Text,
  NumberFormatter,
  Tooltip,
  Stack,
} from "@mantine/core";
import {
  IconCoins,
  IconFileDownload,
  IconStopwatch,
} from "@tabler/icons-react";
import { useStore } from "../store";
import { describeReasoningEffort } from "../_Common/reasoningEffort";

function InferenceStepHeader({ step, ...props }) {
  const { request, response, failedProviderAttempts, deltaMessages } = step;
  const providers = useStore((state) => state.providers);
  const totalMessages = request.messages?.length || 0;
  const deltaCount = deltaMessages?.length || 0;
  const hasToolCalls = response?.toolCalls?.length > 0;
  const isFailed = response?.status === "failure";
  // A failed attempt does not always leave a response row behind — an attempt that never got a
  // reply leaves only a failed request row — so the count the router stamped on the successful
  // response is the reliable signal; the response rows are the detail when they exist.
  const failedAttemptCount = Math.max(
    failedProviderAttempts?.length || 0,
    response?.failedAttempts || 0
  );
  const hadFallback = failedAttemptCount > 0;
  // Requested effort and spent reasoning tokens are the same question asked twice — what we asked
  // the model to do, and what it actually did. They share one badge so the pair is legible: an
  // effort with no tokens behind it is the interesting case, and two separate badges buried it.
  const effort = describeReasoningEffort(
    request.reasoningEffort,
    response?.model || request.model,
    providers
  );
  const reasoningTokens = response?.usage?.reasoningTokens || 0;
  const effortDegraded =
    effort?.state === "clamped" ||
    effort?.state === "raised" ||
    effort?.state === "ignored";

  return (
    <Group align="center" justify="space-between" wrap="nowrap" {...props}>
      <Group gap="xs" wrap="nowrap">
        <Badge size="xs" color="gray" radius="sm" variant="filled">
          {step.stepNumber}
        </Badge>
        <Badge
          size="xs"
          color={isFailed ? "red" : "blue"}
          radius="sm"
          variant="light"
        >
          {isFailed ? "Failed" : "Inference"}
        </Badge>
        {response?.provider && (
          <Badge size="xs" color="grape" radius="sm" variant="light">
            {response.provider}/{response.model}
          </Badge>
        )}
        {hadFallback && (
          <Tooltip
            multiline
            w={360}
            label={
              response?.failedAttemptSummary ||
              `${failedAttemptCount} provider attempt(s) failed before ${response?.provider || "success"}${
                failedProviderAttempts?.length
                  ? `: ${failedProviderAttempts.map((r) => r.provider).join(", ")}`
                  : ""
              }`
            }
          >
            <Badge size="xs" color="yellow" radius="sm" variant="light">
              Fallback ×{failedAttemptCount}
            </Badge>
          </Tooltip>
        )}
        {hasToolCalls && (
          <Text size="xs" c="dimmed">
            → {response.toolCalls.length} tool{" "}
            {response.toolCalls.length === 1 ? "call" : "calls"}
          </Text>
        )}
        {step.toolCallErrors > 0 && (
          <Badge size="xs" color="red" radius="sm" variant="light">
            {step.toolCallErrors} tool{" "}
            {step.toolCallErrors === 1 ? "error" : "errors"}
          </Badge>
        )}
        {deltaCount > 0 && (
          <Text fz="xs" c="dimmed">
            +{deltaCount} new ({totalMessages} total)
          </Text>
        )}
      </Group>
      <Group gap="xs" wrap="nowrap">
        {request.imageFetches?.length > 0 && (
          <Tooltip
            label={
              <Stack gap="xs">
                {request.imageFetches.map((fetch, index) => (
                  <Group key={index} fz="xs" mb="xs">
                    <Badge size="xs">{fetch.statusCode}</Badge>
                    <Badge
                      leftSection={<IconStopwatch size={12} />}
                      size="xs"
                      radius="sm"
                      variant="default"
                    >
                      <NumberFormatter
                        value={fetch.milliseconds}
                        thousandSeparator
                        suffix=" ms"
                      />
                    </Badge>
                    <NumberFormatter
                      value={fetch.bytes}
                      thousandSeparator
                      suffix=" bytes"
                    />
                    <Text ff="monospace" fz="xs">
                      {fetch.url}
                    </Text>
                  </Group>
                ))}
              </Stack>
            }
            withArrow
          >
            <Badge
              leftSection={<IconFileDownload size={12} />}
              size="xs"
              radius="sm"
              variant="default"
            >
              {request.imageFetches.length}
            </Badge>
          </Tooltip>
        )}

        {response?.wasCached ? (
          <Tooltip
            withArrow
            label="Served from response cache — no provider call. The ms reading reflects cache-lookup time, not generation time."
          >
            <Badge size="xs" color="teal" radius="sm" variant="dot">
              cached
              {response?.provider && (
                <>
                  {" · "}
                  {response.provider}
                </>
              )}
              {request.milliseconds > 0 && (
                <>
                  {" · "}
                  <NumberFormatter
                    value={request.milliseconds}
                    thousandSeparator
                    suffix=" ms"
                  />
                </>
              )}
            </Badge>
          </Tooltip>
        ) : (
          (() => {
            // Provider time spans the successful call plus any failed/retried attempts in
            // the fallback chain — keeps fallback latency on the provider side instead of
            // making it look like router overhead.
            const successMs = response?.milliseconds ?? 0;
            const failedMs = response?.failedAttemptsMs ?? 0;
            const providerMs = successMs + failedMs;
            const overhead =
              providerMs > 0 && request.milliseconds > providerMs
                ? request.milliseconds - providerMs
                : 0;
            const showSplit = overhead > 200 || failedMs > 0;
            const badge = (
              <Badge size="xs" color="gray" radius="sm" variant="outline">
                <NumberFormatter
                  value={request.milliseconds}
                  thousandSeparator
                  suffix=" ms"
                />
              </Badge>
            );
            return showSplit ? (
              <Tooltip
                withArrow
                label={
                  <Stack gap={2}>
                    <Text fz="xs">
                      Provider call:{" "}
                      <NumberFormatter
                        thousandSeparator
                        value={successMs}
                        suffix=" ms"
                      />
                    </Text>
                    {failedMs > 0 && (
                      <Text fz="xs">
                        Failed attempts:{" "}
                        <NumberFormatter
                          thousandSeparator
                          value={failedMs}
                          suffix=" ms"
                        />
                      </Text>
                    )}
                    <Text fz="xs">
                      Router overhead:{" "}
                      <NumberFormatter
                        thousandSeparator
                        value={overhead}
                        suffix=" ms"
                      />
                    </Text>
                  </Stack>
                }
              >
                {badge}
              </Tooltip>
            ) : (
              badge
            );
          })()
        )}

        {response?.usage && (
          <Tooltip
            withArrow
            label={
              response.usage.cachedTokensIn ||
              response.usage.cacheCreationTokens ? (
                <Stack gap={2}>
                  <Text fz="xs" fw={600}>
                    Tokens in → out
                  </Text>
                  <Text fz="xs">
                    Non-cached:{" "}
                    <NumberFormatter
                      thousandSeparator
                      value={Math.max(
                        0,
                        response.usage.tokensIn -
                          (response.usage.cachedTokensIn || 0) -
                          (response.usage.cacheCreationTokens || 0)
                      )}
                    />
                  </Text>
                  <Text fz="xs">
                    Cache read:{" "}
                    <NumberFormatter
                      thousandSeparator
                      value={response.usage.cachedTokensIn}
                    />
                  </Text>
                  {response.usage.cacheCreationTokens > 0 && (
                    <Text fz="xs">
                      Cache write:{" "}
                      <NumberFormatter
                        thousandSeparator
                        value={response.usage.cacheCreationTokens}
                      />
                    </Text>
                  )}
                  <Text fz="xs">
                    Out:{" "}
                    <NumberFormatter
                      thousandSeparator
                      value={response.usage.tokensOut}
                    />
                  </Text>
                </Stack>
              ) : (
                "Tokens in → out"
              )
            }
          >
            <Badge size="xs" radius="sm" variant="dot" color="grape">
              <NumberFormatter
                thousandSeparator
                value={response.usage.tokensIn}
              />
              {" → "}
              <NumberFormatter
                thousandSeparator
                value={response.usage.tokensOut}
              />
            </Badge>
          </Tooltip>
        )}

        {(reasoningTokens > 0 || effort) && (
          <Tooltip
            withArrow
            multiline
            w={320}
            label={
              <Stack gap={2}>
                <Text fz="xs" fw={600}>
                  Reasoning
                </Text>
                {effort && <Text fz="xs">{effort.note}</Text>}
                {!effort && (
                  <Text fz="xs">
                    No effort was requested — the provider's own default applied.
                  </Text>
                )}
                <Text fz="xs">
                  {reasoningTokens > 0 ? (
                    <>
                      Spent{" "}
                      <NumberFormatter
                        thousandSeparator
                        value={reasoningTokens}
                      />{" "}
                      reasoning tokens.
                    </>
                  ) : (
                    "The provider reported no reasoning tokens for this step."
                  )}
                </Text>
              </Stack>
            }
          >
            <Badge
              size="xs"
              radius="sm"
              variant="dot"
              color={effortDegraded ? "yellow" : "blue"}
            >
              Reasoning
              {effort && `: ${effort.label}`}
              {(effort?.state === "clamped" || effort?.state === "raised") &&
                ` → ${effort.effectiveLabel}`}
              {effort?.state === "ignored" && " (ignored)"}
              {reasoningTokens > 0 && (
                <>
                  {" · "}
                  <NumberFormatter thousandSeparator value={reasoningTokens} />
                </>
              )}
            </Badge>
          </Tooltip>
        )}

        {response?.usage?.webSearchRequests > 0 && (
          <Badge size="xs" radius="sm" variant="dot" color="orange">
            Searches: {response.usage.webSearchRequests}
          </Badge>
        )}

        {response?.usage?.toolCost > 0 && (
          <Badge size="xs" radius="sm" variant="dot" color="yellow">
            Tools:{" "}
            <NumberFormatter
              thousandSeparator
              value={response.usage.toolCost * 100}
              fixedDecimalScale
              decimalScale={2}
              suffix=" ¢"
            />
          </Badge>
        )}

        {response?.usage?.costCredits > 0 && (
          <Badge
            size="xs"
            rightSection={<IconCoins size={12} />}
            color="red"
            radius="sm"
            variant="dot"
          >
            <NumberFormatter
              value={response.usage.costCredits}
              thousandSeparator
            />
          </Badge>
        )}
      </Group>
    </Group>
  );
}

function ToolCallStepHeader({ step, ...props }) {
  const {
    toolCall,
    completedEvent,
    startedEvent,
    hasError,
    httpErrorCode,
    errorKind,
  } = step;
  const data = completedEvent?.data || startedEvent?.data;
  const durationMs = data?.durationMs;
  const toolType = data?.toolType || startedEvent?.data?.toolType;
  const status = completedEvent?.data?.status;
  const isFailed =
    status === "failed" || completedEvent?.type?.toLowerCase() === "toolfailed";

  return (
    <Group align="center" justify="space-between" wrap="nowrap" {...props}>
      <Group gap="xs" wrap="nowrap">
        <Badge size="xs" color="gray" radius="sm" variant="filled">
          {step.stepNumber}
        </Badge>
        <Badge
          size="xs"
          color={hasError ? "red" : "orange"}
          radius="sm"
          variant="light"
        >
          Tool Call
        </Badge>
        <Text size="sm" fw={500}>
          {toolCall.name}
        </Text>
        {toolType && (
          <Badge size="xs" variant="outline" color="gray">
            {toolType}
          </Badge>
        )}
      </Group>
      <Group gap="xs" wrap="nowrap">
        {durationMs != null && (
          <Badge size="xs" color="gray" radius="sm" variant="outline">
            <NumberFormatter
              value={durationMs}
              thousandSeparator
              suffix=" ms"
            />
          </Badge>
        )}
        {isFailed && !httpErrorCode && (
          <Tooltip
            label={
              errorKind
                ? `The call did not return usable data (${errorKind}). The model was handed the error and carried on.`
                : "The call did not return usable data. The model was handed the error and carried on."
            }
            withArrow
          >
            <Badge size="xs" color="red" variant="light">
              Failed
            </Badge>
          </Tooltip>
        )}
        {httpErrorCode && (
          <Tooltip
            label="The endpoint answered, but with an error status. The model reasoned over the error body — treat any conclusion drawn from this call as unfounded."
            withArrow
          >
            <Badge size="xs" color="red" variant="light">
              {typeof httpErrorCode === "number"
                ? `HTTP ${httpErrorCode}`
                : httpErrorCode}
            </Badge>
          </Tooltip>
        )}
        {!hasError && !isFailed && completedEvent && (
          <Badge size="xs" color="green" variant="light">
            OK
          </Badge>
        )}
        {!completedEvent && !startedEvent && (
          <Text size="xs" c="dimmed" fs="italic">
            No timing data
          </Text>
        )}
      </Group>
    </Group>
  );
}

/**
 * The same step, for a screen with no room to argue.
 *
 * The desktop header is a single nowrap row of up to eleven badges. At 390px that row cannot
 * shrink below its content, so it doesn't — it forces the whole accordion wider than the
 * viewport and every badge collapses to an ellipsis ("I…", "XA…", "CAC…"). Two lines and a
 * shorter list of facts is the fix; the omitted ones (image fetches, cache provenance, search
 * counts, tool surcharges, message deltas) are all still on the desktop header and in the panel
 * below.
 *
 * What is never dropped is anything that says the step went badly — a fallback, a tool error, a
 * degraded reasoning effort. Hiding those to save width would recreate the exact problem the
 * warnings work exists to solve.
 */
function CompactInferenceStepHeader({ step, ...props }) {
  const { request, response, failedProviderAttempts } = step;
  const providers = useStore((state) => state.providers);
  const isFailed = response?.status === "failure";
  const failedAttemptCount = Math.max(
    failedProviderAttempts?.length || 0,
    response?.failedAttempts || 0
  );
  const toolCallCount = response?.toolCalls?.length || 0;
  const usage = response?.usage;
  const effort = describeReasoningEffort(
    request.reasoningEffort,
    response?.model || request.model,
    providers
  );
  const effortDegraded =
    effort?.state === "clamped" ||
    effort?.state === "raised" ||
    effort?.state === "ignored";

  return (
    <Stack gap={4} style={{ minWidth: 0 }} {...props}>
      <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
        <Badge size="xs" color="gray" radius="sm" variant="filled">
          {step.stepNumber}
        </Badge>
        <Badge
          size="xs"
          color={isFailed ? "red" : "blue"}
          radius="sm"
          variant="light"
        >
          {isFailed ? "Failed" : "Inference"}
        </Badge>
        {/* Provider is dropped — the model name is the part that identifies the step, and the
            pair together never fitted. */}
        <Text fz="xs" c="dimmed" truncate style={{ minWidth: 0 }}>
          {response?.model || request.model}
        </Text>
      </Group>
      <Group gap={6} wrap="wrap">
        {failedAttemptCount > 0 && (
          <Badge size="xs" color="yellow" radius="sm" variant="light">
            Fallback ×{failedAttemptCount}
          </Badge>
        )}
        {step.toolCallErrors > 0 && (
          <Badge size="xs" color="red" radius="sm" variant="light">
            {step.toolCallErrors} tool{" "}
            {step.toolCallErrors === 1 ? "error" : "errors"}
          </Badge>
        )}
        {effortDegraded && (
          <Badge size="xs" color="yellow" radius="sm" variant="light">
            {effort.label}
            {effort.state === "ignored"
              ? " (ignored)"
              : ` → ${effort.effectiveLabel}`}
          </Badge>
        )}
        {toolCallCount > 0 && (
          <Text fz="xs" c="dimmed">
            → {toolCallCount} tool {toolCallCount === 1 ? "call" : "calls"}
          </Text>
        )}
        {request.milliseconds > 0 && (
          <Text fz="xs" c="dimmed">
            <NumberFormatter
              value={request.milliseconds}
              thousandSeparator
              suffix=" ms"
            />
          </Text>
        )}
        {usage && (
          <Text fz="xs" c="dimmed">
            <NumberFormatter thousandSeparator value={usage.tokensIn} />
            {" → "}
            <NumberFormatter thousandSeparator value={usage.tokensOut} />
          </Text>
        )}
        {usage?.costCredits > 0 && (
          <Group gap={2} wrap="nowrap">
            <IconCoins size={11} color="var(--mantine-color-dimmed)" />
            <Text fz="xs" c="dimmed">
              <NumberFormatter thousandSeparator value={usage.costCredits} />
            </Text>
          </Group>
        )}
      </Group>
    </Stack>
  );
}

function CompactToolCallStepHeader({ step, ...props }) {
  const {
    toolCall,
    completedEvent,
    startedEvent,
    hasError,
    httpErrorCode,
    errorKind,
  } = step;
  const data = completedEvent?.data || startedEvent?.data;
  const durationMs = data?.durationMs;
  const toolType = data?.toolType;
  const isFailed =
    completedEvent?.data?.status === "failed" ||
    completedEvent?.type?.toLowerCase() === "toolfailed";

  return (
    <Stack gap={4} style={{ minWidth: 0 }} {...props}>
      <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
        <Badge size="xs" color="gray" radius="sm" variant="filled">
          {step.stepNumber}
        </Badge>
        <Badge
          size="xs"
          color={hasError ? "red" : "orange"}
          radius="sm"
          variant="light"
        >
          Tool
        </Badge>
        <Text fz="sm" fw={500} truncate style={{ minWidth: 0 }}>
          {toolCall.name}
        </Text>
      </Group>
      <Group gap={6} wrap="wrap">
        {/* The error kind is spelled out inline rather than left in a tooltip: there is no hover
            on a phone, and this is the one badge worth reading. */}
        {httpErrorCode && (
          <Badge size="xs" color="red" radius="sm" variant="light">
            {typeof httpErrorCode === "number"
              ? `HTTP ${httpErrorCode}`
              : httpErrorCode}
          </Badge>
        )}
        {isFailed && !httpErrorCode && (
          <Badge size="xs" color="red" radius="sm" variant="light">
            Failed{errorKind ? `: ${errorKind}` : ""}
          </Badge>
        )}
        {!hasError && !isFailed && completedEvent && (
          <Badge size="xs" color="green" radius="sm" variant="light">
            OK
          </Badge>
        )}
        {toolType && (
          <Text fz="xs" c="dimmed">
            {toolType}
          </Text>
        )}
        {durationMs != null && (
          <Text fz="xs" c="dimmed">
            <NumberFormatter
              value={durationMs}
              thousandSeparator
              suffix=" ms"
            />
          </Text>
        )}
        {!completedEvent && !startedEvent && (
          <Text fz="xs" c="dimmed" fs="italic">
            No timing data
          </Text>
        )}
      </Group>
    </Stack>
  );
}

export default function StepHeader({ step, compact = false, ...props }) {
  if (step.type === "inference") {
    return compact ? (
      <CompactInferenceStepHeader step={step} {...props} />
    ) : (
      <InferenceStepHeader step={step} {...props} />
    );
  }
  return compact ? (
    <CompactToolCallStepHeader step={step} {...props} />
  ) : (
    <ToolCallStepHeader step={step} {...props} />
  );
}
